'use client';

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useBasePath } from '../navigation/base-path';
import { S3Image } from './s3-image';
import { TrendVideoMedia } from './trend-video-media';
import { cn } from '../utils/cn';

export interface HomeTrendsTabExample {
  imageUrl: string;
  thumbnailUrl?: string | null;
  isVideo: boolean;
}

export interface HomeTrendsTabItem {
  id: string;
  name: string;
  category?: string | null;
  badgeText?: string | null;
  badgeColor?: string | null;
  badgeBorder?: boolean | null;
  examples: HomeTrendsTabExample[];
}

interface HomeTrendsTabContentProps {
  trends?: HomeTrendsTabItem[];
  featured?: HomeTrendsTabItem[];
  categories?: string[];
  isActive: boolean;
  isLoading?: boolean;
}

export function HomeTrendsTabContent({
  trends,
  featured = [],
  categories,
  isActive,
  isLoading = false,
}: HomeTrendsTabContentProps) {
  const trendsByCategory = useMemo(() => {
    if (!trends) return {};
    return trends.reduce(
      (accumulator, trend) => {
        const category = trend.category || 'Другое';
        if (!accumulator[category]) accumulator[category] = [];
        accumulator[category].push(trend);
        return accumulator;
      },
      {} as Record<string, HomeTrendsTabItem[]>,
    );
  }, [trends]);

  const categoryOrder = categories || Object.keys(trendsByCategory);
  const basePath = useBasePath();

  if (isLoading || !trends) {
    return (
      <div className="space-y-4">
        {[1, 2].map((row) => (
          <div key={row} className="flex gap-2 overflow-x-auto scrollbar-hide">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-60 w-40 flex-shrink-0 animate-pulse rounded-[20px_4px_20px_4px] bg-card"
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {featured.length > 0 && <FeaturedMarquee items={featured} isActive={isActive} />}
      {categoryOrder.map((category) => {
        const categoryTrends = trendsByCategory[category];
        if (!categoryTrends || categoryTrends.length === 0) return null;

        return (
          <div key={category}>
            <div className="mb-2 flex items-center justify-between px-1">
              <h3 className="font-benzin text-lg font-semibold text-white">{category}</h3>
              <Link
                href={`${basePath}/trends?category=${encodeURIComponent(category)}`}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#222326]/80"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 4l6 6-6 6" />
                  <line x1="4" y1="10" x2="15" y2="10" />
                </svg>
              </Link>
            </div>
            <div className="flex gap-2 overflow-x-auto py-1 scrollbar-hide">
              {categoryTrends.map((trend) => (
                <TrendCard key={trend.id} trend={trend} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const TrendCard = memo(function TrendCard({ trend }: { trend: HomeTrendsTabItem }) {
  const example = trend.examples[0];
  const basePath = useBasePath();
  if (!example) return null;

  const borderColor = trend.badgeColor ?? '#f1b11d';
  const borderColorLight = `${borderColor}66`;
  const borderColorEnd = `${borderColor}aa`;

  return (
    <Link
      href={`${basePath}/trends/${trend.id}`}
      className={cn(
        'group relative h-60 w-40 flex-shrink-0 overflow-hidden rounded-[20px_4px_20px_4px]',
        trend.badgeBorder && 'p-[1px]',
      )}
      style={
        trend.badgeBorder
          ? {
              background: `linear-gradient(180deg, ${borderColorLight}, ${borderColorEnd})`,
            }
          : undefined
      }
    >
      <div
        className={cn(
          trend.badgeBorder
            ? 'relative h-full w-full overflow-hidden rounded-[19px_3px_19px_3px]'
            : 'relative h-full w-full',
        )}
      >
        {example.isVideo ? (
          <TrendVideoMedia
            src={example.imageUrl}
            poster={example.thumbnailUrl}
            alt={trend.name}
          />
        ) : (
          <S3Image
            src={example.imageUrl}
            alt={trend.name}
            fill
            className="object-cover"
            showSkeleton={false}
            fadeDuration={180}
          />
        )}
        {trend.badgeText && (
          <div
            className="absolute right-0 top-0 rounded-[0_4px_0_10px] px-2 py-0.5 text-[11px] font-bold text-white"
            style={{ backgroundColor: trend.badgeColor ?? '#f1b11d' }}
          >
            {trend.badgeText}
          </div>
        )}
        <div className="absolute bottom-0 left-0 h-[115px] w-full bg-gradient-to-t from-black/[0.87] to-transparent opacity-70" />
        <div className="absolute bottom-2.5 left-2.5 right-2.5 font-benzin text-base font-semibold leading-tight text-white">
          {trend.name}
        </div>
      </div>
    </Link>
  );
});

TrendCard.displayName = 'TrendCard';

const FeaturedMarquee = memo(function FeaturedMarquee({
  items,
  isActive,
}: {
  items: HomeTrendsTabItem[];
  isActive: boolean;
}) {
  const middle = Math.ceil(items.length / 2);
  const topRow = useMemo(() => items.slice(0, middle), [items, middle]);
  const bottomRow = useMemo(() => items.slice(middle), [items, middle]);

  return (
    <div className="space-y-2 overflow-hidden py-1">
      <MarqueeRow items={topRow} direction="left" isActive={isActive} />
      {bottomRow.length > 0 && (
        <MarqueeRow items={bottomRow} direction="right" isActive={isActive} />
      )}
    </div>
  );
});

FeaturedMarquee.displayName = 'FeaturedMarquee';

const MarqueeRow = memo(function MarqueeRow({
  items,
  direction,
  isActive,
}: {
  items: HomeTrendsTabItem[];
  direction: 'left' | 'right';
  isActive: boolean;
}) {
  const tripled = useMemo(() => [...items, ...items, ...items], [items]);
  const itemSignature = useMemo(() => items.map((item) => item.id).join('|'), [items]);
  const trackRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const positionRef = useRef(0);
  const loopWidthRef = useRef(0);
  const autoSpeed = direction === 'left' ? -0.4 : 0.4;
  const velocityRef = useRef(0);
  const pausedUntilRef = useRef(0);
  const touchRef = useRef<{
    x: number;
    y: number;
    pos: number;
    moved: boolean;
    locked: 'x' | 'y' | null;
    lastX: number;
    lastTime: number;
  } | null>(null);
  const [isDocumentVisible, setIsDocumentVisible] = useState(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );

  useEffect(() => {
    const handleVisibilityChange = () =>
      setIsDocumentVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const syncTrackWidth = () => {
      const previousWidth = loopWidthRef.current;
      const nextWidth = track.scrollWidth / 3;
      if (nextWidth <= 0) return;

      loopWidthRef.current = nextWidth;
      if (previousWidth === 0) {
        positionRef.current = -nextWidth;
      } else {
        positionRef.current = wrapMarqueePosition(positionRef.current, nextWidth);
      }
      track.style.transform = `translate3d(${positionRef.current}px, 0, 0)`;
    };

    syncTrackWidth();
    const resizeObserver = new ResizeObserver(syncTrackWidth);
    resizeObserver.observe(track);

    return () => resizeObserver.disconnect();
  }, [itemSignature]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const shouldAnimate = isActive && isDocumentVisible;
    if (!shouldAnimate) {
      track.style.transform = `translate3d(${positionRef.current}px, 0, 0)`;
      return;
    }

    let previousTime = performance.now();

    const tick = (now: number) => {
      const dt = Math.min(now - previousTime, 32);
      previousTime = now;
      const width = loopWidthRef.current;

      if (!touchRef.current) {
        if (Math.abs(velocityRef.current) > 0.001) {
          positionRef.current += velocityRef.current * dt;
          velocityRef.current *= 0.97;
          if (Math.abs(velocityRef.current) < 0.001) velocityRef.current = 0;
        } else if (now > pausedUntilRef.current) {
          positionRef.current += autoSpeed * dt * 0.06;
        }
      }

      positionRef.current = wrapMarqueePosition(positionRef.current, width);
      track.style.transform = `translate3d(${positionRef.current}px, 0, 0)`;
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [autoSpeed, isActive, isDocumentVisible, itemSignature]);

  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    velocityRef.current = 0;
    touchRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      pos: positionRef.current,
      moved: false,
      locked: null,
      lastX: touch.clientX,
      lastTime: performance.now(),
    };
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch || !touchRef.current) return;
    const ref = touchRef.current;
    const dx = touch.clientX - ref.x;
    const dy = touch.clientY - ref.y;

    if (!ref.locked) {
      if (Math.abs(dx) > 5) ref.locked = 'x';
      else if (Math.abs(dy) > 5) ref.locked = 'y';
      else return;
    }
    if (ref.locked === 'y') return;

    ref.moved = true;
    positionRef.current = ref.pos + dx;
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${positionRef.current}px, 0, 0)`;
    }

    const now = performance.now();
    const elapsed = now - ref.lastTime;
    if (elapsed > 0) {
      velocityRef.current = (touch.clientX - ref.lastX) / elapsed;
      ref.lastX = touch.clientX;
      ref.lastTime = now;
    }
  };

  const onTouchEnd = () => {
    if (touchRef.current?.moved) {
      const blocker = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      wrapperRef.current?.addEventListener('click', blocker, { capture: true, once: true });
      setTimeout(
        () => wrapperRef.current?.removeEventListener('click', blocker, { capture: true }),
        100,
      );
    }
    if (!touchRef.current?.moved) velocityRef.current = 0;
    touchRef.current = null;
    pausedUntilRef.current = performance.now() + 3000;
  };

  return (
    <div ref={wrapperRef} className="select-none overflow-hidden [touch-action:pan-y]">
      <div
        ref={trackRef}
        className="flex w-max gap-2 will-change-transform [backface-visibility:hidden]"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {tripled.map((trend, index) => (
          <TrendCard key={`${trend.id}-${index}`} trend={trend} />
        ))}
      </div>
    </div>
  );
});

MarqueeRow.displayName = 'MarqueeRow';

function wrapMarqueePosition(value: number, width: number): number {
  if (width <= 0) return value;
  const mod = ((value % width) + width) % width;
  return mod === 0 ? 0 : mod - width;
}
